import React, { useState } from 'react';
import { Alert, Spinner, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faTrashAlt,
  faFileUpload,
  faFilePdf,
  faFileImage,
  faSortUp,
  faSortDown,
  faEye,
  faEllipsisV,
  faCopy
} from "@fortawesome/free-solid-svg-icons";
import SongPartManager from '../../components/songs/SongPartManager';
import SongPartPreview from '../../components/songs/SongPartPreview';
import SongCopyModal from '../../components/songs/SongCopyModal';
import LikeButton from '../../components/LikeButton';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Component for displaying the song table with sorting and actions
 */
const SongTable = ({
  filteredSongs,
  sortField,
  sortDirection,
  handleSort,
  handleOpenModal,
  handleDelete,
  canEditSong,
  loading,
  error,
  refreshSongs,
}) => {
  const { t } = useLanguage();

  // State for the song part manager, preview, and copy modals
  const [showPartModal, setShowPartModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  
  // Make sure handleDelete exists before using it
  const onDeleteClick = (songId) => {
    if (typeof handleDelete === 'function') {
      handleDelete(songId);
    } else {
    }
  };
  
  // Open the part file manager modal
  const openPartManager = (song) => {
    setSelectedSong(song);
    setShowPartModal(true);
  };

  // Open the copy modal
  const openCopyModal = (song) => {
    setSelectedSong(song);
    setShowCopyModal(true);
  };

  // Handle copy complete
  const handleCopyComplete = () => {
    if (refreshSongs) {
      refreshSongs();
    }
  };
  
  // Handle part file update
  const handlePartUpdated = (updatedSong) => {
    // Refresh the song list to show updated part file status
    if (refreshSongs) {
      refreshSongs();
    }
  };
  
  // Get icon for part file type
  const getPartFileIcon = (song) => {
    if (!song.has_part_file) {
      return faFileUpload;
    }
    
    if (song.part_file_type === 'pdf') {
      return faFilePdf;
    } else {
      return faFileImage;
    }
  };
  
  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">{t('songbook.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (filteredSongs.length === 0) {
    return (
      <Alert variant="info">
        {t('songbook.table.noResults')}
      </Alert>
    );
  }
  
  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ width: '40px' }}></th>
              <th
                className="col-4"
                onClick={() => handleSort('title')}
                style={{ cursor: 'pointer' }}
              >
                {t('songbook.table.title')}
                {sortField === 'title' && (
                  <FontAwesomeIcon
                    icon={sortDirection === 'asc' ? faSortUp : faSortDown}
                    className="ms-1"
                  />
                )}
              </th>
              <th
                className="col-3"
                onClick={() => handleSort('composer')}
                style={{ cursor: 'pointer' }}
              >
                {t('songbook.table.composer')}
                {sortField === 'composer' && (
                  <FontAwesomeIcon
                    icon={sortDirection === 'asc' ? faSortUp : faSortDown}
                    className="ms-1"
                  />
                )}
              </th>
              <th className="col-2">{t('songbook.table.tags')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredSongs.map(song => (
              <tr key={song.id} className="align-middle">
                <td style={{ width: '40px', textAlign: 'center' }}>
                  <LikeButton contentType="song" contentId={song.id} size="sm" />
                </td>
                <td>{song.title}</td>
                <td>{song.composer || '-'}</td>
                <td>
                  <div className="d-flex flex-wrap gap-1">
                    {song.tags && song.tags.length > 0 ? (
                      song.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="badge bg-outline-secondary"
                          style={{
                            fontSize: '0.75em',
                            fontWeight: 'normal',
                            border: '1px solid #6c757d',
                            color: '#6c757d',
                            background: 'transparent',
                          }}
                        >
                          #{tag.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.85em' }}>-</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center justify-content-end">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as="span"
                        id={`song-actions-${song.id}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon icon={faEllipsisV} className="text-secondary fs-5" />
                      </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {/* Preview notes - only if part file exists */}
                      {song.has_part_file && (
                        <Dropdown.Item onClick={() => {
                          setSelectedSong(song);
                          setShowPreviewModal(true);
                        }}>
                          <FontAwesomeIcon icon={faEye} className="me-2 text-success" />
                          {t('songbook.table.previewNotes')}
                        </Dropdown.Item>
                      )}

                      {/* Manage notes */}
                      <Dropdown.Item onClick={() => openPartManager(song)}>
                        <FontAwesomeIcon
                          icon={getPartFileIcon(song)}
                          className={`me-2 ${song.has_part_file ? (song.part_file_type === 'pdf' ? 'text-danger' : 'text-primary') : ''}`}
                        />
                        {song.has_part_file ? t('songbook.table.manageNotes') : t('songbook.table.uploadNotes')}
                      </Dropdown.Item>

                      {/* Edit/Copy/Delete - only for users with edit permissions */}
                      {canEditSong(song) && (
                        <>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => handleOpenModal(song)}>
                            <FontAwesomeIcon icon={faPencilAlt} className="me-2" />
                            {t('songbook.table.editSong')}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => openCopyModal(song)}>
                            <FontAwesomeIcon icon={faCopy} className="me-2" />
                            {t('songbook.table.copyTo')}
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => onDeleteClick(song.id)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                            {t('songbook.table.deleteSong')}
                          </Dropdown.Item>
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            {t('songbook.table.showing', { count: filteredSongs.length })}
          </small>
        </div>
      </div>
      
      {/* Song Part Manager Modal */}
      <SongPartManager 
        show={showPartModal}
        onHide={() => setShowPartModal(false)}
        song={selectedSong}
        onPartUpdated={handlePartUpdated}
      />
      
      {/* Song Part Preview Modal */}
      <SongPartPreview
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        song={selectedSong}
      />

      {/* Song Copy Modal */}
      <SongCopyModal
        show={showCopyModal}
        onHide={() => setShowCopyModal(false)}
        song={selectedSong}
        onCopyComplete={handleCopyComplete}
      />
    </>
  );
};

export default SongTable;